/*
  Carnevore Mountain hero refresh: the hero's photo, video and second photo (the cat).

  Taking turns:
  - With a video: the video plays through once, the second photo fades in for "Seconds per photo",
    then the video plays again from the start.
  - Without a video, or while the video can't play: the two photos swap every "Seconds per photo".

  Video:
  - The first photo is the page's main image and loads first. The video file is only requested after
    the page has finished loading, so it never slows the first view (or the Core Web Vitals score).
  - Skipped, leaving the photos, when the visitor has data saver on or is on a 2G connection. If the
    phone blocks autoplay (Low Power Mode on an iPhone, for example) or the file fails, the photos stay.
  - The video fades in only once it is actually playing, so there is never a black or empty frame.

  For everything:
  - Nothing moves for visitors who have turned off motion: they see the first photo.
  - Waits while the hero is off screen or the tab is hidden; the photo swap also waits while the
    pointer or keyboard focus is on it.
  - The pause button stops it for good, as accessibility guidelines ask for anything that moves on
    its own for more than 5 seconds.
*/
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Files to try, best first. Sized files (uploaded videos): the smallest that is still sharp in the
  // frame, then larger, then smaller. Theme files (no size): in the order given, WebM before MP4.
  function orderSources(video, sources, frame) {
    const playable = sources.filter((source) => source.src && video.canPlayType(source.type));
    const sized = playable.filter((source) => source.height > 0).sort((a, b) => a.height - b.height);
    if (!sized.length) return playable;
    const needed = frame.clientHeight * Math.min(window.devicePixelRatio || 1, 2);
    const sharp = sized.filter((source) => source.height >= needed);
    const soft = sized.filter((source) => source.height < needed).reverse();
    return sharp.concat(soft);
  }

  function readSources(figure) {
    const data = figure.querySelector('[data-cm-video-sources]');
    if (!data) return [];
    try {
      return JSON.parse(data.textContent);
    } catch (error) {
      return [];
    }
  }

  function setUp(figure) {
    if (figure.dataset.cmHeroReady) return;
    figure.dataset.cmHeroReady = 'true';
    if (reducedMotion.matches) return;

    const button = figure.querySelector('[data-cm-rotate-toggle]');
    const frame = figure.querySelector('.cm-lead__frame') || figure;
    const next = figure.querySelector('.cm-lead__img--next');
    const rotates = 'cmRotate' in figure.dataset && Boolean(next);
    const delay = Math.max(3000, parseInt(figure.dataset.cmRotate, 10) || 6000);

    const video = 'cmVideo' in figure.dataset ? figure.querySelector('[data-cm-video-el]') : null;
    const connection = navigator.connection;
    const saveData = connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || ''));
    const queue = video && !saveData ? orderSources(video, readSources(figure), frame) : [];
    const hasVideo = queue.length > 0;

    if (!rotates && !hasVideo) return;
    // The second photo loads lazily; ask for it now so it is ready for the first fade.
    if (rotates) next.loading = 'eager';

    let paused = false;
    let onScreen = true;
    let hovered = false;
    let videoLive = false; // true once the video has really played
    let showingNext = false;
    let timer = null;

    const running = () => !paused && onScreen && !document.hidden;
    const clearTimer = () => {
      window.clearTimeout(timer);
      timer = null;
    };
    const show = (second) => {
      showingNext = second;
      figure.classList.toggle('is-next', second);
    };
    const playVideo = () => {
      const attempt = video.play();
      if (attempt) attempt.catch(() => {});
    };

    // One step of the turn-taking, scheduled only while it should run.
    const schedule = () => {
      clearTimer();
      if (!rotates || !running()) return;
      if (videoLive) {
        // The video's own end moves things on; only the second photo needs a timer.
        if (showingNext) timer = window.setTimeout(backToVideo, delay);
      } else if (!hovered) {
        timer = window.setTimeout(() => {
          show(!showingNext);
          schedule();
        }, delay);
      }
    };

    function backToVideo() {
      video.currentTime = 0;
      show(false);
      if (running()) playVideo();
    }

    const sync = () => {
      if (hasVideo && video.getAttribute('src')) {
        // Before its first play the video may start whatever is showing; it brings itself forward.
        if (running() && (!videoLive || !showingNext)) playVideo();
        else if (!video.paused) video.pause();
      }
      schedule();
    };

    if (button) {
      button.hidden = !rotates;
      const label = () => {
        const thing = rotates ? 'slideshow' : 'video';
        button.setAttribute('aria-label', (paused ? 'Play ' : 'Pause ') + thing);
      };
      label();
      button.addEventListener('click', () => {
        paused = !paused;
        button.setAttribute('aria-pressed', String(paused));
        label();
        sync();
      });
    }

    figure.addEventListener('pointerenter', () => { hovered = true; schedule(); });
    figure.addEventListener('pointerleave', () => { hovered = false; schedule(); });
    figure.addEventListener('focusin', () => { hovered = true; schedule(); });
    figure.addEventListener('focusout', () => { hovered = false; schedule(); });
    document.addEventListener('visibilitychange', sync);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        onScreen = entries[0].isIntersecting;
        sync();
      }).observe(figure);
    }

    if (hasVideo) {
      // Belt and braces for iOS: muted must be a property as well as an attribute before play().
      video.muted = true;
      // Taking turns: play once, then hand over to the second photo. Alone: loop forever.
      video.loop = !rotates;

      video.addEventListener('playing', () => {
        if (!videoLive) {
          videoLive = true;
          // If the photo swap had moved on to the second photo, come back to the video.
          show(false);
          figure.classList.add('is-playing');
          if (button) button.hidden = false;
          schedule();
        }
      });
      video.addEventListener('ended', () => {
        if (!rotates) return;
        show(true);
        schedule();
      });
      // A file the browser says it can play but can't (some phones and WebM): move on to the next one.
      video.addEventListener('error', () => {
        if (queue.length && !videoLive) {
          video.src = queue.shift().src;
          playVideo();
        }
      });

      const start = () => {
        video.src = queue.shift().src;
        if (running()) playVideo();
      };
      // Wait for the page to finish loading, then for a quiet moment, before asking for the video.
      const whenIdle = () =>
        window.requestIdleCallback ? window.requestIdleCallback(start, { timeout: 2000 }) : window.setTimeout(start, 200);
      if (document.readyState === 'complete') whenIdle();
      else window.addEventListener('load', whenIdle, { once: true });
    }

    // Until (or unless) the video plays, the photos take turns on their own.
    schedule();
  }

  const init = (root) => root.querySelectorAll('[data-cm-rotate], [data-cm-video]').forEach(setUp);
  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
